"use client";

/**
 * SolutionsJourney — v4 (Alternating Rich Rows)
 *
 * Narrative: "Tu arrives avec un CV. Tu repars avec un contrat."
 *
 * Layout: 3 alternating full-width rows (Stripe / Linear pattern):
 *   - Row 1 (Prépare):   text LEFT  | rich mockup RIGHT
 *   - Row 2 (Postule):   mockup LEFT | text RIGHT
 *   - Row 3 (Décroche):  text LEFT  | rich mockup RIGHT
 *
 * Each row is DENSE & visually rich (big number watermark, eyebrow badge,
 * display title, description, tool chips, mini CTA). Each mockup panel is
 * a composition of 3 overlapping tilted cards with per-phase colored glow.
 *
 * Goal: no empty space. Every row feels like a full product teaser.
 */
import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import { Briefcase, FileSignature, FileText, Linkedin, Mail, MessageSquare, Mic, Puzzle, Search, Sparkles, Trophy } from "lucide-react";

/* ──────────────────────────────────────────────
   Per-phase rich mockups
   ────────────────────────────────────────────── */

function MockupPrepare() {
  const $ = _c(35);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute inset-4 -z-10 rounded-full bg-[#818cf8]/[0.12] blur-[60px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: 20,
      rotate: -5
    };
    t2 = {
      opacity: 1,
      y: 0,
      rotate: -3
    };
    t3 = {
      once: true,
      margin: "-80px"
    };
    t4 = {
      duration: 0.6,
      delay: 0.1
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#818cf8] to-[#6366f1] text-[10px] font-bold text-white">MD</div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mb-3 flex items-center justify-between"><div className="flex items-center gap-2">{t5}<div className="leading-tight"><div className="text-[11px] font-semibold">Marie Dubois</div><div className="text-[9px] text-muted-foreground">Product Manager · Paris</div></div></div><span className="rounded-md bg-[#818cf8]/15 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-[#818cf8]">v3</span></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mb-1 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground/70">Expérience</div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center gap-1.5"><span className="h-0.5 w-0.5 rounded-full bg-[#818cf8]" /><span className="font-medium">Lead PM</span><span className="text-muted-foreground">· Airbnb · 2021</span></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="mb-2.5">{t7}<div className="space-y-0.5 text-[10px] leading-snug text-foreground/85">{t8}<div className="flex items-center gap-1.5"><span className="h-0.5 w-0.5 rounded-full bg-[#818cf8]" /><span className="font-medium">PM Senior</span><span className="text-muted-foreground">· Stripe · 2019</span></div></div></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mb-1 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground/70">Compétences</div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="mb-2">{t10}<div className="flex flex-wrap gap-1">{["Growth", "SQL", "Figma", "Product"].map(_temp)}</div></div>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Sparkles className="h-3 w-3 shrink-0 text-[#818cf8]" />;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <motion.div initial={t1} whileInView={t2} viewport={t3} transition={t4} className="absolute left-[2%] top-[4%] w-[70%] rounded-2xl border border-border/50 bg-card/90 p-4 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(129,140,248,0.4)]">{t6}{t9}{t11}<div className="flex items-center gap-1.5 rounded-md border border-[#818cf8]/30 bg-[#818cf8]/10 px-2 py-1.5">{t12}<span className="text-[9px] leading-tight text-foreground/85"><span className="font-semibold">+8 pts ATS</span> · Ajoute « Leadership »</span></div></motion.div>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 0,
      y: 20,
      rotate: 5
    };
    t15 = {
      opacity: 1,
      y: 0,
      rotate: 4
    };
    t16 = {
      once: true,
      margin: "-80px"
    };
    t17 = {
      duration: 0.6,
      delay: 0.25
    };
    $[14] = t14;
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
  } else {
    t14 = $[14];
    t15 = $[15];
    t16 = $[16];
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="mb-2.5 flex items-center justify-between"><div className="leading-tight"><div className="text-[10px] font-semibold">Score ATS</div><div className="text-[9px] text-muted-foreground">Meta · Senior PM</div></div><Search className="h-3 w-3 text-[#818cf8]" /></div>;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <circle cx="28" cy="28" r="23" stroke="oklch(0.25 0.07 280)" strokeWidth="5" fill="none" />;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  let t21;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">{t19}<motion.circle cx="28" cy="28" r="23" stroke="#818cf8" strokeWidth="5" fill="none" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 23}`} initial={{
        strokeDashoffset: 2 * Math.PI * 23
      }} whileInView={{
        strokeDashoffset: 2 * Math.PI * 23 * 0.08
      }} viewport={{
        once: true
      }} transition={{
        duration: 1.5,
        delay: 0.8,
        ease: "easeOut"
      }} /></svg>;
    t21 = <div className="font-display text-2xl font-bold leading-none">92%</div>;
    $[20] = t20;
    $[21] = t21;
  } else {
    t20 = $[20];
    t21 = $[21];
  }
  let t22;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="mb-3 flex items-center gap-3">{t20}<div>{t21}<div className="mt-0.5 flex items-center gap-0.5 text-[9px] font-medium text-[#818cf8]"><span>↑</span><span>+14 vs v2</span></div></div></div>;
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <motion.div initial={t14} whileInView={t15} viewport={t16} transition={t17} className="absolute right-[0%] top-[30%] w-[54%] rounded-2xl border border-border/50 bg-card/90 p-3.5 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(129,140,248,0.4)]">{t18}{t22}<div className="space-y-1">{[{
          label: "Mots-cl\xE9s",
          value: 94
        }, {
          label: "Format",
          value: 88
        }, {
          label: "Structure",
          value: 95
        }].map(_temp2)}</div></motion.div>;
    $[23] = t23;
  } else {
    t23 = $[23];
  }
  let t24;
  let t25;
  let t26;
  let t27;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      opacity: 0,
      y: 20,
      rotate: -3
    };
    t25 = {
      opacity: 1,
      y: 0,
      rotate: -2
    };
    t26 = {
      once: true,
      margin: "-80px"
    };
    t27 = {
      duration: 0.6,
      delay: 0.4
    };
    $[24] = t24;
    $[25] = t25;
    $[26] = t26;
    $[27] = t27;
  } else {
    t24 = $[24];
    t25 = $[25];
    t26 = $[26];
    t27 = $[27];
  }
  let t28;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#818cf8]/15 text-[#818cf8]"><Mail className="h-3.5 w-3.5" /></div>;
    $[28] = t28;
  } else {
    t28 = $[28];
  }
  let t29;
  let t30;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="mb-2.5 flex items-center justify-between"><div className="flex items-center gap-2">{t28}<div className="leading-tight"><div className="text-[11px] font-semibold">Lettre · Airbnb</div><div className="text-[9px] text-muted-foreground">Ton professionnel</div></div></div><span className="rounded-full border border-[#10b981]/30 bg-[#10b981]/10 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-[#10b981]">Prêt</span></div>;
    t30 = <div className="mb-2 text-[10px] font-medium leading-snug text-foreground/90">Chers membres de l'équipe Airbnb,</div>;
    $[29] = t29;
    $[30] = t30;
  } else {
    t29 = $[29];
    t30 = $[30];
  }
  let t31;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <motion.div initial={{
      width: 0
    }} whileInView={{
      width: "94%"
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8,
      delay: 0.9
    }} className="h-1.5 rounded-full bg-muted/50" />;
    $[31] = t31;
  } else {
    t31 = $[31];
  }
  let t32;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <motion.div initial={{
      width: 0
    }} whileInView={{
      width: "82%"
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.8,
      delay: 1.1
    }} className="h-1.5 rounded-full bg-muted/50" />;
    $[32] = t32;
  } else {
    t32 = $[32];
  }
  let t33;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="mb-2.5 space-y-1">{t31}{t32}<motion.div initial={{
        width: 0
      }} whileInView={{
        width: "88%"
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 1.3
      }} className="h-1.5 rounded-full bg-muted/50" /></div>;
    $[33] = t33;
  } else {
    t33 = $[33];
  }
  let t34;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="relative aspect-[4/5] w-full max-w-[460px] lg:aspect-[5/6] lg:w-full">{t0}{t13}{t23}<motion.div initial={t24} whileInView={t25} viewport={t26} transition={t27} className="absolute bottom-[2%] left-[12%] w-[68%] rounded-2xl border border-border/50 bg-card/90 p-4 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(129,140,248,0.4)]">{t29}{t30}{t33}<div className="flex items-center justify-between rounded-md border border-border/40 bg-muted/20 px-2 py-1 text-[9px]"><div className="flex items-center gap-1.5"><Sparkles className="h-2.5 w-2.5 text-[#818cf8]" /><span className="text-muted-foreground">3 versions · tons variés</span></div><span className="font-semibold text-[#818cf8]">247 mots</span></div></motion.div></div>;
    $[34] = t34;
  } else {
    t34 = $[34];
  }
  return t34;
}
function _temp2(item) {
  return <div key={item.label} className="flex items-center gap-1.5 text-[9px]"><span className="w-[45px] text-muted-foreground">{item.label}</span><div className="h-0.5 flex-1 overflow-hidden rounded-full bg-muted/40"><motion.div initial={{
        width: 0
      }} whileInView={{
        width: `${item.value}%`
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 1
      }} className="h-full rounded-full bg-[#818cf8]" /></div><span className="w-[22px] text-right font-semibold tabular-nums text-[#818cf8]">{item.value}</span></div>;
}
function _temp(skill) {
  return <span key={skill} className="rounded border border-[#818cf8]/30 bg-[#818cf8]/10 px-1.5 py-0.5 text-[9px] font-medium text-[#818cf8]">{skill}</span>;
}
function MockupPostule() {
  const $ = _c(32);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="absolute inset-4 -z-10 rounded-full bg-[#a855f7]/[0.12] blur-[60px]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: 20,
      rotate: 4
    };
    t2 = {
      opacity: 1,
      y: 0,
      rotate: 3
    };
    t3 = {
      once: true,
      margin: "-80px"
    };
    t4 = {
      duration: 0.6,
      delay: 0.1
    };
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex gap-1"><span className="h-2 w-2 rounded-full bg-[#ff5f56]/60" /><span className="h-2 w-2 rounded-full bg-[#ffbd2e]/60" /><span className="h-2 w-2 rounded-full bg-[#27c93f]/60" /></div>;
    t6 = <div className="ml-2 flex-1 truncate rounded bg-background/60 px-2 py-0.5 text-[9px] text-muted-foreground">linkedin.com/jobs/view/…</div>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      scale: [1, 1.15, 1]
    };
    t8 = {
      duration: 1.8,
      repeat: Infinity
    };
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex items-center gap-1.5 border-b border-border/50 bg-muted/40 px-3 py-2">{t5}{t6}<motion.div animate={t7} transition={t8} className="flex h-4 w-4 items-center justify-center rounded bg-[#a855f7]/30"><Puzzle className="h-2.5 w-2.5 text-[#a855f7]" /></motion.div></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="text-[11px] font-semibold leading-tight text-foreground">Senior Product Manager</div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground"><span>Stripe</span><span>·</span><span>Paris</span><span>·</span><span className="font-medium text-foreground/80">€90–120k</span></div>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  let t13;
  let t14;
  let t15;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      opacity: 0,
      y: -4
    };
    t13 = {
      opacity: 1,
      y: 0
    };
    t14 = {
      once: true
    };
    t15 = {
      duration: 0.4,
      delay: 0.9
    };
    $[12] = t12;
    $[13] = t13;
    $[14] = t14;
    $[15] = t15;
  } else {
    t12 = $[12];
    t13 = $[13];
    t14 = $[14];
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <motion.div initial={t1} whileInView={t2} viewport={t3} transition={t4} className="absolute right-[4%] top-[4%] w-[62%] overflow-hidden rounded-2xl border border-border/50 bg-card/85 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(168,85,247,0.4)]">{t9}<div className="space-y-1.5 p-3">{t10}{t11}<motion.div initial={t12} whileInView={t13} viewport={t14} transition={t15} className="mt-2 flex items-center justify-between gap-1.5 rounded-md border border-[#a855f7]/40 bg-[#a855f7]/10 px-2 py-1 text-[9px] font-semibold text-[#a855f7]"><div className="flex items-center gap-1.5"><span className="relative flex h-1.5 w-1.5 rounded-full bg-[#a855f7]"><span className="absolute h-full w-full animate-ping rounded-full bg-[#a855f7]" /></span>Importé · 1 clic</div><span className="text-[8px] font-normal opacity-80">+CV adapté</span></motion.div></div></motion.div>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      opacity: 0,
      y: 20,
      rotate: -4
    };
    t18 = {
      opacity: 1,
      y: 0,
      rotate: -3
    };
    t19 = {
      once: true,
      margin: "-80px"
    };
    t20 = {
      duration: 0.6,
      delay: 0.25
    };
    $[17] = t17;
    $[18] = t18;
    $[19] = t19;
    $[20] = t20;
  } else {
    t17 = $[17];
    t18 = $[18];
    t19 = $[19];
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#a855f7] to-[#7c3aed] text-[10px] font-bold text-white">MD</div>;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="mb-2.5 flex items-center gap-2">{t21}<div className="flex-1 leading-tight"><div className="text-[11px] font-semibold">Mon profil</div><div className="text-[9px] text-muted-foreground">347 vues · +14% ce mois</div></div><Linkedin className="h-3.5 w-3.5 text-[#a855f7]" /></div>;
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="font-medium">Headline :</span>;
    $[23] = t23;
  } else {
    t23 = $[23];
  }
  let t24;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="mb-2 rounded-md bg-muted/30 px-2 py-1.5 text-[9px] leading-snug text-foreground/85">{t23}{" "}<span className="text-muted-foreground">Senior PM · Growth · Ex-Stripe</span></div>;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <motion.div initial={t17} whileInView={t18} viewport={t19} transition={t20} className="absolute left-[2%] top-[30%] w-[55%] rounded-2xl border border-border/50 bg-card/90 p-3.5 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(168,85,247,0.4)]">{t22}{t24}<div className="space-y-1.5">{[{
          label: "Headline",
          value: 90,
          before: 68
        }, {
          label: "R\xE9sum\xE9",
          value: 72,
          before: 45
        }, {
          label: "Exp\xE9rience",
          value: 85,
          before: 62
        }].map(_temp3)}</div></motion.div>;
    $[25] = t25;
  } else {
    t25 = $[25];
  }
  let t26;
  let t27;
  let t28;
  let t29;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      opacity: 0,
      y: 20,
      rotate: 3
    };
    t27 = {
      opacity: 1,
      y: 0,
      rotate: 2
    };
    t28 = {
      once: true,
      margin: "-80px"
    };
    t29 = {
      duration: 0.6,
      delay: 0.4
    };
    $[26] = t26;
    $[27] = t27;
    $[28] = t28;
    $[29] = t29;
  } else {
    t26 = $[26];
    t27 = $[27];
    t28 = $[28];
    t29 = $[29];
  }
  let t30;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="mb-3 flex items-center justify-between"><div className="leading-tight"><div className="text-[11px] font-semibold">Candidatures</div><div className="text-[9px] text-muted-foreground">12 actives cette semaine</div></div><Briefcase className="h-3.5 w-3.5 text-[#a855f7]" /></div>;
    $[30] = t30;
  } else {
    t30 = $[30];
  }
  let t31;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="relative aspect-[4/5] w-full max-w-[460px] lg:aspect-[5/6] lg:w-full">{t0}{t16}{t25}<motion.div initial={t26} whileInView={t27} viewport={t28} transition={t29} className="absolute bottom-[2%] right-[4%] w-[70%] rounded-2xl border border-border/50 bg-card/90 p-4 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(168,85,247,0.4)]">{t30}<div className="grid grid-cols-3 gap-1.5">{[{
            label: "Postul\xE9",
            count: 4,
            color: "#818cf8",
            items: ["Meta", "Notion", "Figma", "Alan"]
          }, {
            label: "Entretien",
            count: 2,
            color: "#a855f7",
            items: ["Stripe", "Spotify"]
          }, {
            label: "Offre",
            count: 1,
            color: "#d946ef",
            items: ["Airbnb"]
          }].map(_temp4)}</div></motion.div></div>;
    $[31] = t31;
  } else {
    t31 = $[31];
  }
  return t31;
}
function _temp4(col, i_0) {
  return <div key={col.label} className="space-y-1"><div className="flex items-center justify-between text-[8px]"><span className="font-medium uppercase tracking-wider text-muted-foreground">{col.label}</span><span className="rounded-sm px-1 py-0.5 text-[7px] font-bold" style={{
        background: `${col.color}20`,
        color: col.color
      }}>{col.count}</span></div>{col.items.map((company, j) => <motion.div key={j} initial={{
      opacity: 0,
      x: -4
    }} whileInView={{
      opacity: 1,
      x: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: 0.9 + i_0 * 0.1 + j * 0.06
    }} className="flex h-5 items-center overflow-hidden rounded border border-border/50 bg-muted/20 pr-1.5"><div className="h-full w-[3px]" style={{
        background: col.color
      }} /><span className="ml-1.5 truncate text-[9px] font-medium text-foreground/85">{company}</span></motion.div>)}</div>;
}
function _temp3(s, i) {
  return <div key={s.label}><div className="mb-0.5 flex items-center justify-between text-[9px]"><span className="text-muted-foreground">{s.label}</span><div className="flex items-center gap-1"><span className="text-muted-foreground/50 line-through">{s.before}</span><span className="font-semibold text-[#a855f7]">{s.value}</span></div></div><div className="h-1 overflow-hidden rounded-full bg-muted/40"><motion.div initial={{
        width: 0
      }} whileInView={{
        width: `${s.value}%`
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.7 + i * 0.15
      }} className="h-full rounded-full bg-[#a855f7]" /></div></div>;
}
function MockupDecroche() {
  const $ = _c(32);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [0.3, 0.8, 0.5, 1, 0.6, 0.9, 0.4, 0.7, 0.5, 0.8, 0.3, 0.6];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const bars = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-4 -z-10 rounded-full bg-[#d946ef]/[0.12] blur-[60px]" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y: 20,
      rotate: -4
    };
    t3 = {
      opacity: 1,
      y: 0,
      rotate: -3
    };
    t4 = {
      once: true,
      margin: "-80px"
    };
    t5 = {
      duration: 0.6,
      delay: 0.1
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#d946ef]/15 text-[#d946ef]"><MessageSquare className="h-4 w-4" /></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mb-3 flex items-center justify-between"><div className="flex items-center gap-2">{t6}<div className="leading-tight"><div className="text-[11px] font-semibold">Entretien · BCG</div><div className="text-[9px] text-muted-foreground">Question 3 / 12</div></div></div><span className="rounded-md bg-[#10b981]/15 px-1.5 py-0.5 text-[9px] font-bold text-[#10b981]">8.4/10</span></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex gap-1.5"><span className="mt-0.5 text-[8px] font-bold uppercase tracking-wider text-muted-foreground">IA</span><div className="flex-1 rounded-lg rounded-tl-sm bg-[#d946ef]/15 px-2.5 py-1.5 text-[9px] leading-snug">Parle-moi d'un cas où tu as dû structurer une décision complexe.</div></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex justify-end gap-1.5"><div className="flex-1 rounded-lg rounded-tr-sm bg-muted/50 px-2.5 py-1.5 text-[9px] leading-snug">Lors d'un audit pour un retailer, j'ai appliqué la méthode MECE…</div><span className="mt-0.5 text-[8px] font-bold uppercase tracking-wider text-muted-foreground">Toi</span></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: [0.3, 1, 0.3]
    };
    t11 = {
      duration: 1.4,
      repeat: Infinity
    };
    $[10] = t10;
    $[11] = t11;
  } else {
    t10 = $[10];
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="space-y-1.5">{t8}{t9}<motion.div animate={t10} transition={t11} className="flex w-fit gap-1 rounded-lg rounded-tl-sm bg-[#d946ef]/15 px-2.5 py-2 ml-5"><span className="h-1 w-1 rounded-full bg-[#d946ef]" /><span className="h-1 w-1 rounded-full bg-[#d946ef]" /><span className="h-1 w-1 rounded-full bg-[#d946ef]" /></motion.div></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <motion.div initial={t2} whileInView={t3} viewport={t4} transition={t5} className="absolute left-[2%] top-[4%] w-[66%] rounded-2xl border border-border/50 bg-card/90 p-4 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(217,70,239,0.4)]">{t7}{t12}<div className="mt-3 flex flex-wrap gap-1"><span className="rounded-full border border-[#10b981]/30 bg-[#10b981]/10 px-1.5 py-0.5 text-[8px] font-semibold text-[#10b981]">✓ Méthode STAR</span><span className="rounded-full border border-[#10b981]/30 bg-[#10b981]/10 px-1.5 py-0.5 text-[8px] font-semibold text-[#10b981]">✓ Exemple concret</span></div></motion.div>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: 0,
      y: 20,
      rotate: 4
    };
    t15 = {
      opacity: 1,
      y: 0,
      rotate: 3
    };
    t16 = {
      once: true,
      margin: "-80px"
    };
    t17 = {
      duration: 0.6,
      delay: 0.25
    };
    $[14] = t14;
    $[15] = t15;
    $[16] = t16;
    $[17] = t17;
  } else {
    t14 = $[14];
    t15 = $[15];
    t16 = $[16];
    t17 = $[17];
  }
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="relative"><div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#d946ef]/20 text-[#d946ef]"><Mic className="h-4 w-4" /></div><span className="absolute -inset-1 animate-ping rounded-full bg-[#d946ef]/30" /></div>;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="text-[11px] font-semibold">Simulation vocale</div>;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="mb-2.5 flex items-center gap-2">{t18}<div className="flex-1 leading-tight">{t19}<div className="flex items-center gap-1 text-[9px]"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" /><span className="text-red-500 font-medium">REC · 01:42</span></div></div><span className="rounded-md bg-[#d946ef]/15 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-[#d946ef]">Pro</span></div>;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="mb-2.5 flex h-8 items-end gap-[3px]">{bars.map(_temp5)}</div>;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <motion.div initial={t14} whileInView={t15} viewport={t16} transition={t17} className="absolute right-[2%] top-[36%] w-[56%] rounded-2xl border border-border/50 bg-card/90 p-3.5 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(217,70,239,0.4)]">{t20}{t21}<div className="grid grid-cols-3 gap-1 text-[9px]">{[{
          label: "Clart\xE9",
          value: "92%",
          color: "#10b981"
        }, {
          label: "Rythme",
          value: "Bon",
          color: "#10b981"
        }, {
          label: "Filler",
          value: "3\xD7",
          color: "#f59e0b"
        }].map(_temp6)}</div></motion.div>;
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  let t24;
  let t25;
  let t26;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      opacity: 0,
      y: 20,
      rotate: -3
    };
    t24 = {
      opacity: 1,
      y: 0,
      rotate: -2
    };
    t25 = {
      once: true,
      margin: "-80px"
    };
    t26 = {
      duration: 0.6,
      delay: 0.4
    };
    $[23] = t23;
    $[24] = t24;
    $[25] = t25;
    $[26] = t26;
  } else {
    t23 = $[23];
    t24 = $[24];
    t25 = $[25];
    t26 = $[26];
  }
  let t27;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#d946ef] to-[#a855f7] text-white shadow-[0_8px_24px_-4px_rgba(217,70,239,0.6)]"><FileSignature className="h-5 w-5" /></div>;
    $[27] = t27;
  } else {
    t27 = $[27];
  }
  let t28;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="mb-3 flex items-center gap-2.5">{t27}<div className="flex-1 leading-tight"><div className="font-display text-sm font-bold">Offre signée 🎉</div><div className="text-[10px] text-muted-foreground">Il y a 2 jours</div></div><Trophy className="h-4 w-4 text-[#d946ef]" /></div>;
    $[28] = t28;
  } else {
    t28 = $[28];
  }
  let t29;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="text-[11px] font-semibold leading-tight">Senior Product Manager</div>;
    $[29] = t29;
  } else {
    t29 = $[29];
  }
  let t30;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="mb-3 rounded-lg bg-muted/30 px-2.5 py-2">{t29}<div className="mt-0.5 flex items-center gap-1 text-[9px] text-muted-foreground"><span>Airbnb</span><span>·</span><span>Paris</span><span>·</span><span className="font-medium text-foreground/80">CDI</span><span>·</span><span className="font-semibold text-[#d946ef]">+32% salaire</span></div></div>;
    $[30] = t30;
  } else {
    t30 = $[30];
  }
  let t31;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="relative aspect-[4/5] w-full max-w-[460px] lg:aspect-[5/6] lg:w-full">{t1}{t13}{t22}<motion.div initial={t23} whileInView={t24} viewport={t25} transition={t26} className="absolute bottom-[2%] left-[10%] w-[72%] rounded-2xl border-2 border-[#d946ef]/50 bg-gradient-to-br from-card/95 via-[#d946ef]/5 to-primary/5 p-4 backdrop-blur-xl shadow-[0_32px_60px_-18px_rgba(217,70,239,0.55)]">{t28}{t30}<div className="flex items-center gap-2 rounded-lg border border-[#d946ef]/30 bg-[#d946ef]/10 px-2.5 py-1.5"><Sparkles className="h-3 w-3 shrink-0 text-[#d946ef]" /><span className="text-[10px] font-semibold leading-tight text-[#d946ef]">3 entretiens préparés · CV v3 · Lettre adaptée</span></div></motion.div></div>;
    $[31] = t31;
  } else {
    t31 = $[31];
  }
  return t31;
}

/* ──────────────────────────────────────────────
   Row spec
   ────────────────────────────────────────────── */
function _temp6(m) {
  return <div key={m.label} className="rounded-md border border-border/40 bg-muted/20 px-1.5 py-1"><div className="text-[8px] text-muted-foreground">{m.label}</div><div className="font-semibold leading-none" style={{
      color: m.color
    }}>{m.value}</div></div>;
}
function _temp5(h, i) {
  return <motion.span key={i} className="flex-1 rounded-sm bg-[#d946ef]" initial={{
    scaleY: 0.2
  }} animate={{
    scaleY: [0.2, h, 0.3, h * 0.8, 0.5]
  }} transition={{
    duration: 1.2,
    delay: i * 0.05,
    repeat: Infinity
  }} style={{
    transformOrigin: "bottom",
    height: "100%"
  }} />;
}
type Tool = {
  icon: React.ReactNode;
  name: string;
  status?: "live" | "premium" | "soon";
};
type PhaseRow = {
  number: string;
  label: string;
  eyebrow: string;
  title: string;
  description: string;
  tools: Tool[];
  accent: string;
  mockup: React.ReactNode;
  href: string;
};
function getRows(locale: string, isAuthenticated: boolean): PhaseRow[] {
  const fr = locale === "fr";
  return [{
    number: "01",
    label: fr ? "Prépare" : "Prepare",
    eyebrow: fr ? "Étape 01 · Tu arrives" : "Step 01 · You arrive",
    title: fr ? "Un dossier prêt à envoyer." : "An application-ready dossier.",
    description: fr ? "Upload ton CV. Velyq l'analyse, l'optimise pour chaque offre, et génère les lettres qui vont avec — tout en 3 minutes." : "Upload your CV. Velyq analyzes it, optimizes for every role, and generates matching letters — all in 3 minutes.",
    tools: [{
      icon: <FileText className="h-3.5 w-3.5" />,
      name: fr ? "Création de CV" : "Resume Builder",
      status: "live"
    }, {
      icon: <Search className="h-3.5 w-3.5" />,
      name: fr ? "Analyse ATS" : "ATS Analysis",
      status: "live"
    }, {
      icon: <Mail className="h-3.5 w-3.5" />,
      name: fr ? "Lettres de motivation" : "Cover Letters",
      status: "soon"
    }],
    accent: "#818cf8",
    mockup: <MockupPrepare />,
    href: isAuthenticated ? "/resumes" : "/register"
  }, {
    number: "02",
    label: fr ? "Postule" : "Apply",
    eyebrow: fr ? "Étape 02 · Tu candidates" : "Step 02 · You apply",
    title: fr ? "Des candidatures suivies, jamais perdues." : "Applications tracked, never lost.",
    description: fr ? "Extension Chrome pour importer n'importe quelle offre en 1 clic. LinkedIn optimisé. Kanban qui track entretien, relances, offres." : "Chrome extension to import any role in 1 click. Optimized LinkedIn. Kanban tracking interviews, follow-ups, offers.",
    tools: [{
      icon: <Puzzle className="h-3.5 w-3.5" />,
      name: fr ? "Extension Chrome" : "Chrome Extension",
      status: "soon"
    }, {
      icon: <Linkedin className="h-3.5 w-3.5" />,
      name: fr ? "Optimisation LinkedIn" : "LinkedIn Optimizer",
      status: "soon"
    }, {
      icon: <Briefcase className="h-3.5 w-3.5" />,
      name: fr ? "Suivi des candidatures" : "Application tracker",
      status: "soon"
    }],
    accent: "#a855f7",
    mockup: <MockupPostule />,
    href: "#"
  }, {
    number: "03",
    label: fr ? "Décroche" : "Land",
    eyebrow: fr ? "Étape 03 · Tu décroches" : "Step 03 · You land it",
    title: fr ? "Un entretien maîtrisé, une offre signée." : "Interview mastered, offer signed.",
    description: fr ? "Simulations d'entretien adaptées à ton secteur. Voix IA en temps réel pour t'entraîner comme en vrai. Feedback sur ta voix, ton rythme, ta clarté." : "Interview simulations tailored to your industry. Real-time AI voice to practice like the real thing. Feedback on delivery, pace, clarity.",
    tools: [{
      icon: <MessageSquare className="h-3.5 w-3.5" />,
      name: fr ? "Préparation d'entretien" : "Interview Prep",
      status: "live"
    }, {
      icon: <Mic className="h-3.5 w-3.5" />,
      name: fr ? "Simulation vocale" : "Voice Simulation",
      status: "premium"
    }],
    accent: "#d946ef",
    mockup: <MockupDecroche />,
    href: isAuthenticated ? "/interviews/new" : "/register"
  }];
}

/* ──────────────────────────────────────────────
   Single row layout (alternating)
   ────────────────────────────────────────────── */

function PhaseRowBlock(t0) {
  const $ = _c(55);
  const {
    row,
    index
  } = t0;
  const isEven = index % 2 === 0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      y: 30
    };
    t2 = {
      opacity: 1,
      y: 0
    };
    t3 = {
      once: true,
      margin: "-80px"
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== row.accent) {
    t5 = {
      color: row.accent
    };
    $[4] = row.accent;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== row.number || $[7] !== t5) {
    t6 = <span aria-hidden={true} className="pointer-events-none absolute -left-4 -top-8 select-none font-display text-[140px] font-black leading-none opacity-[0.07] sm:text-[180px] lg:-left-8 lg:-top-12 lg:text-[220px]" style={t5}>{row.number}</span>;
    $[6] = row.number;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `${row.accent}44`;
  const t8 = `${row.accent}0F`;
  let t9;
  if ($[9] !== row.accent || $[10] !== t7 || $[11] !== t8) {
    t9 = {
      borderColor: t7,
      background: t8,
      color: row.accent
    };
    $[9] = row.accent;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== row.accent) {
    t10 = <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{
      background: row.accent
    }} />;
    $[13] = row.accent;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== row.accent) {
    t11 = <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{
      background: row.accent
    }} />;
    $[15] = row.accent;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11) {
    t12 = <span className="relative flex h-1.5 w-1.5" aria-hidden={true}>{t10}{t11}</span>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== row.eyebrow) {
    t13 = <span className="font-mono uppercase tracking-[0.15em]">{row.eyebrow}</span>;
    $[20] = row.eyebrow;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t12 || $[23] !== t13 || $[24] !== t9) {
    t14 = <div className="mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium" style={t9}>{t12}{t13}</div>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t9;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== row.title) {
    t15 = <h3 className="font-display text-3xl font-bold leading-[1.1] tracking-tight text-balance sm:text-4xl md:text-5xl">{row.title}</h3>;
    $[26] = row.title;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== row.description) {
    t16 = <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground">{row.description}</p>;
    $[28] = row.description;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== row.accent || $[31] !== row.tools) {
    let t18;
    if ($[33] !== row.accent) {
      t18 = tool => <div key={tool.name} className="group inline-flex items-center gap-2 rounded-xl border border-border/60 bg-card/40 px-3.5 py-2 transition-all duration-200 hover:border-border hover:bg-card/70" style={{
        boxShadow: `inset 0 1px 0 0 ${row.accent}10`
      }}><span className="flex h-6 w-6 items-center justify-center rounded-md" style={{
          background: `${row.accent}22`,
          color: row.accent
        }}>{tool.icon}</span><span className="text-sm font-medium">{tool.name}</span>{tool.status === "premium" && <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{
          background: `${row.accent}22`,
          color: row.accent
        }}>Pro</span>}{tool.status === "soon" && <span className="rounded-full border border-border/60 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">Soon</span>}</div>;
      $[33] = row.accent;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    t17 = row.tools.map(t18);
    $[30] = row.accent;
    $[31] = row.tools;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[35] !== t17) {
    t18 = <div className="mt-7 flex flex-wrap gap-2">{t17}</div>;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t14 || $[38] !== t15 || $[39] !== t16 || $[40] !== t18) {
    t19 = <div className="relative">{t14}{t15}{t16}{t18}</div>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t19 || $[43] !== t6) {
    t20 = <motion.div initial={t1} whileInView={t2} viewport={t3} transition={t4} className="relative flex flex-col justify-center">{t6}{t19}</motion.div>;
    $[42] = t19;
    $[43] = t6;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const textBlock = t20;
  let t21;
  let t22;
  let t23;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      opacity: 0,
      scale: 0.95
    };
    t22 = {
      opacity: 1,
      scale: 1
    };
    t23 = {
      once: true,
      margin: "-80px"
    };
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t21 = $[45];
    t22 = $[46];
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      duration: 0.7,
      delay: 0.15,
      ease: [0.16, 1, 0.3, 1]
    };
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== row.mockup) {
    t25 = <motion.div initial={t21} whileInView={t22} viewport={t23} transition={t24} className="relative flex justify-center">{row.mockup}</motion.div>;
    $[49] = row.mockup;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  const mockupBlock = t25;
  let t26;
  if ($[51] !== isEven || $[52] !== mockupBlock || $[53] !== textBlock) {
    t26 = <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">{isEven ? <>{textBlock}{mockupBlock}</> : <><div className="order-2 lg:order-1">{mockupBlock}</div><div className="order-1 lg:order-2">{textBlock}</div></>}</div>;
    $[51] = isEven;
    $[52] = mockupBlock;
    $[53] = textBlock;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  return t26;
}

/* ──────────────────────────────────────────────
   Main
   ────────────────────────────────────────────── */

type SolutionsJourneyProps = {
  locale: string;
  isAuthenticated: boolean;
};
export function SolutionsJourney(t0) {
  const $ = _c(61);
  const {
    locale,
    isAuthenticated
  } = t0;
  const fr = locale === "fr";
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== fr || $[1] !== isAuthenticated || $[2] !== locale) {
    const rows = getRows(locale, isAuthenticated);
    t5 = "solutions";
    t6 = "relative overflow-hidden py-20 sm:py-28 lg:py-32";
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="pointer-events-none absolute inset-0 -z-10"><div className="absolute left-[8%] top-[15%] h-[500px] w-[500px] rounded-full bg-[#818cf8]/[0.05] blur-[140px]" /><div className="absolute right-[10%] top-[45%] h-[500px] w-[500px] rounded-full bg-[#a855f7]/[0.06] blur-[140px]" /><div className="absolute left-[8%] bottom-[10%] h-[500px] w-[500px] rounded-full bg-[#d946ef]/[0.05] blur-[140px]" /></div>;
      t8 = <div className="noise absolute inset-0 -z-10 opacity-50" />;
      $[11] = t7;
      $[12] = t8;
    } else {
      t7 = $[11];
      t8 = $[12];
    }
    t3 = "container mx-auto px-4";
    let t10;
    let t11;
    let t12;
    let t9;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        opacity: 0,
        y: 20
      };
      t10 = {
        opacity: 1,
        y: 0
      };
      t11 = {
        once: true,
        margin: "-80px"
      };
      t12 = {
        duration: 0.6
      };
      $[13] = t10;
      $[14] = t11;
      $[15] = t12;
      $[16] = t9;
    } else {
      t10 = $[13];
      t11 = $[14];
      t12 = $[15];
      t9 = $[16];
    }
    let t13;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Sparkles className="h-3 w-3 text-primary" />;
      $[17] = t13;
    } else {
      t13 = $[17];
    }
    const t14 = fr ? "Un \xE9cosyst\xE8me. Pas 8 outils \xE9parpill\xE9s." : "One ecosystem. Not 8 scattered tools.";
    let t15;
    if ($[18] !== t14) {
      t15 = <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/60 bg-card/50 px-3 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">{t13}<span>{t14}</span></div>;
      $[18] = t14;
      $[19] = t15;
    } else {
      t15 = $[19];
    }
    let t16;
    if ($[20] !== fr) {
      t16 = <h2 className="font-display text-3xl font-bold leading-[1.05] tracking-tight text-balance sm:text-5xl md:text-[3.5rem]">{fr ? <>Tu arrives avec un CV.<br /><span className="bg-gradient-to-br from-[#818cf8] via-primary to-[#d946ef] bg-clip-text text-transparent">Tu repars avec un contrat.</span></> : <>Arrive with a resume.<br /><span className="bg-gradient-to-br from-[#818cf8] via-primary to-[#d946ef] bg-clip-text text-transparent">Leave with a signed offer.</span></>}</h2>;
      $[20] = fr;
      $[21] = t16;
    } else {
      t16 = $[21];
    }
    const t17 = fr ? "Velyq couvre chaque \xE9tape de ta recherche. On ne te l\xE2che pas entre deux \xE9tapes \u2014 tu arrives avec une id\xE9e, tu repars avec le poste." : "Velyq covers every step. We don't drop you between stages \u2014 arrive with an idea, leave with the role.";
    let t18;
    if ($[22] !== t17) {
      t18 = <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">{t17}</p>;
      $[22] = t17;
      $[23] = t18;
    } else {
      t18 = $[23];
    }
    if ($[24] !== t15 || $[25] !== t16 || $[26] !== t18) {
      t4 = <motion.div initial={t9} whileInView={t10} viewport={t11} transition={t12} className="mx-auto max-w-3xl text-center">{t15}{t16}{t18}</motion.div>;
      $[24] = t15;
      $[25] = t16;
      $[26] = t18;
      $[27] = t4;
    } else {
      t4 = $[27];
    }
    t1 = "mx-auto mt-20 flex max-w-6xl flex-col gap-28 sm:mt-24 sm:gap-32 lg:gap-36";
    t2 = rows.map(_temp7);
    $[0] = fr;
    $[1] = isAuthenticated;
    $[2] = locale;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[28] !== t1 || $[29] !== t2) {
    t9 = <div className={t1}>{t2}</div>;
    $[28] = t1;
    $[29] = t2;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: 0,
      y: 16
    };
    t11 = {
      opacity: 1,
      y: 0
    };
    t12 = {
      once: true,
      margin: "-60px"
    };
    t13 = {
      duration: 0.5,
      delay: 0.3
    };
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
    t13 = $[34];
  }
  let t14;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span className="font-display text-2xl font-black text-primary">8</span>;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = fr ? "outils int\xE9gr\xE9s" : "integrated tools";
  let t16;
  if ($[36] !== t15) {
    t16 = <div className="flex items-center gap-2.5">{t14}<span className="text-muted-foreground">{t15}</span></div>;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="h-1.5 w-1.5 rounded-full bg-border" />;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="font-display text-2xl font-black text-primary">1</span>;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = fr ? "seul \xE9cosyst\xE8me" : "single ecosystem";
  let t20;
  if ($[40] !== t19) {
    t20 = <div className="flex items-center gap-2.5">{t18}<span className="text-muted-foreground">{t19}</span></div>;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span className="h-1.5 w-1.5 rounded-full bg-border" />;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="font-display text-2xl font-black text-primary">0</span>;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const t23 = fr ? "autre app \xE0 installer" : "other app needed";
  let t24;
  if ($[44] !== t23) {
    t24 = <div className="flex items-center gap-2.5">{t22}<span className="text-muted-foreground">{t23}</span></div>;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t16 || $[47] !== t20 || $[48] !== t24) {
    t25 = <motion.div initial={t10} whileInView={t11} viewport={t12} transition={t13} className="mx-auto mt-20 flex max-w-4xl flex-wrap items-center justify-center gap-x-10 gap-y-4 rounded-2xl border border-border/50 bg-card/40 px-6 py-5 text-sm backdrop-blur-sm">{t16}{t17}{t20}{t21}{t24}</motion.div>;
    $[46] = t16;
    $[47] = t20;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t25 || $[51] !== t3 || $[52] !== t4 || $[53] !== t9) {
    t26 = <div className={t3}>{t4}{t9}{t25}</div>;
    $[50] = t25;
    $[51] = t3;
    $[52] = t4;
    $[53] = t9;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t26 || $[56] !== t5 || $[57] !== t6 || $[58] !== t7 || $[59] !== t8) {
    t27 = <section id={t5} className={t6}>{t7}{t8}{t26}</section>;
    $[55] = t26;
    $[56] = t5;
    $[57] = t6;
    $[58] = t7;
    $[59] = t8;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  return t27;
}
function _temp7(row, i) {
  return <PhaseRowBlock key={row.number} row={row} index={i} />;
}